import React, { useMemo, useState } from 'react';

import { Song } from 'src/@types/api-types';

import {
  getCurrentTimeInSong,
  getPixelSizeNumberFromSizingFactor,
} from 'components/b2c/Mp3KbpPlayer/Mp3KbpPlayer.utils';
import { useMediaElementListener } from 'services/hooks/useMediaElementListener';
import { PlayerSongDetails } from 'shared-types/player-event';

import {
  MentionSlideContainer,
  SIZING_FACTORS,
  MentionSlideText,
  TitleContainer,
  WritersOrPublishersContainer,
} from './MentionSlide.style';
import {
  getPublishersLabel,
  getWritersLabel,
  shouldDisplayMentionSlide,
} from './MentionSlide.utils';
import { FormattedMessage, useIntl } from 'react-intl';

interface Props {
  videoElement: HTMLVideoElement;
  isPremium: boolean;
  songDetails: Song;
  videoAreaHeight: number;
  currentSongDetails?: PlayerSongDetails | null;
}

const MentionSlide: React.FC<Props> = ({
  videoElement,
  isPremium,
  songDetails,
  videoAreaHeight,
  currentSongDetails,
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const intl = useIntl();

  const titleFontSize = useMemo(
    () => getPixelSizeNumberFromSizingFactor(videoAreaHeight, SIZING_FACTORS.titleFontSize),
    [videoAreaHeight],
  );
  const writersFontSize = useMemo(
    () => getPixelSizeNumberFromSizingFactor(videoAreaHeight, SIZING_FACTORS.writersFontSize),
    [videoAreaHeight],
  );
  const publishersFontSize = useMemo(
    () => getPixelSizeNumberFromSizingFactor(videoAreaHeight, SIZING_FACTORS.publishersFontSize),
    [videoAreaHeight],
  );

  const title = songDetails.title;
  const writers = getWritersLabel(songDetails.writers.map((artist) => artist.name));
  const publishers = getPublishersLabel(songDetails.publishers.map((publisher) => publisher.name));

  useMediaElementListener(videoElement, 'timeupdate', () => {
    setIsVisible(shouldDisplayMentionSlide(getCurrentTimeInSong(videoElement, isPremium)));
  });

  return (
    <MentionSlideContainer isVisible={isVisible}>
      {currentSongDetails?.singerName && (
        <MentionSlideText
          textContent={`${intl.formatMessage({id: 'song.sing-by'})} ${currentSongDetails.singerName}`}
          fontSizeNumber={writersFontSize}
          marginBottom
        >
          <FormattedMessage id={'song.sing-by'} /> {currentSongDetails.singerName}
        </MentionSlideText>
      )}
      <TitleContainer>
        <MentionSlideText textContent={title} fontSizeNumber={titleFontSize}>
          {title}
        </MentionSlideText>
      </TitleContainer>
      <WritersOrPublishersContainer>
        <MentionSlideText textContent={writers} fontSizeNumber={writersFontSize}>
          {writers}
        </MentionSlideText>
      </WritersOrPublishersContainer>
      <WritersOrPublishersContainer>
        <MentionSlideText textContent={publishers} fontSizeNumber={publishersFontSize}>
          {publishers}
        </MentionSlideText>
      </WritersOrPublishersContainer>
    </MentionSlideContainer>
  );
};

export default MentionSlide;
